export const STATUS_OPTIONS = [
  { value: 'draft', label: '草稿' },
  { value: 'active', label: '进行中' },
  { value: 'paused', label: '已暂停' },
  { value: 'archived', label: '已归档' },
]

export const OBJECTIVE_OPTIONS = [
  { value: 'traffic', label: '吸引更多网站访客（适用于流量）' },
  { value: 'sales', label: '增加消息互动量（适用于销售额）' },
  { value: 'engagement', label: '提升网站销量（适用于销售额）' },
  // { value: 'leads', label: '收集潜在客户' },
  // { value: 'awareness', label: '提升品牌认知' },
]

export const PREVIEW_MODES = [
  { value: 'feed', label: 'Feed' },
  { value: 'reels', label: 'Reels' },
  { value: 'messenger', label: 'Messenger' },
]

export const CTA_OPTIONS = [
  { value: 'Shop Now', label: '立即购买' },
  { value: 'Learn More', label: '了解更多' },
  { value: 'Send Message', label: '发送消息' },
  { value: 'Get Started', label: '立即开始' },
]

export const AGE_MIN = 18
export const AGE_MAX = 60

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

export function normalizeAgeRange(value) {
  if (Array.isArray(value) && value.length === 2) {
    const start = clamp(Number(value[0] || AGE_MIN), AGE_MIN, AGE_MAX)
    const end = clamp(Number(value[1] || AGE_MAX), AGE_MIN, AGE_MAX)
    return start <= end ? [start, end] : [end, start]
  }

  const text = String(value || '').trim()
  if (!text) return [AGE_MIN, AGE_MAX]

  if (/^\d+\+$/.test(text)) {
    const start = clamp(Number(text.replace('+', '')), AGE_MIN, AGE_MAX)
    return [start, AGE_MAX]
  }

  const match = text.match(/^(\d+)\s*-\s*(\d+)$/)
  if (match) {
    const start = clamp(Number(match[1]), AGE_MIN, AGE_MAX)
    const end = clamp(Number(match[2]), AGE_MIN, AGE_MAX)
    return start <= end ? [start, end] : [end, start]
  }

  return [AGE_MIN, AGE_MAX]
}

export function formatAgeRange(value) {
  const range = normalizeAgeRange(value)
  return `${range[0]}-${range[1]}`
}

export function createAd(slot) {
  return {
    slot,
    name: `素材 ${slot}`,
    status: 'draft',
    format: 'image',
    media_url: '',
    headline: '',
    primary_text: '',
    description: '',
    cta: 'Shop Now',
    landing_url: '',
    utm: {
      source: 'facebook',
      medium: 'paid_social',
      campaign: '',
      content: `ad_${slot}`,
      term: '',
    },
  }
}

export function createDefaultForm(commercialId = '') {
  return {
    id: null,
    commercial_id: commercialId,
    latest_payment_status: '',
    latest_order_id: null,
    latest_settlement_amount: null,
    latest_settlement_currency: '',
    latest_payment_created_at: null,
    latest_paid_at: null,
    campaign: {
      name: '',
      objective: 'traffic',
      status: 'draft',
      budget_type: 'lifetime',
      budget_amount: 120,
      currency: 'USD',
      start_time: '',
      end_time: '',
    },
    ad_set: {
      name: '',
      conversion_location: 'messages',
      optimization_goal: 'landing_page_views',
      landing_url: '',
      audience: {
        regions: ['US'],
        age: [AGE_MIN, AGE_MAX],
        gender: 'all',
      },
      placements: ['facebook_feed'],
    },
    ads: [createAd(1)],
  }
}

export function normalizeStatus(status) {
  const value = String(status || '').trim().toLowerCase()
  if (value === 'ended') return 'archived'
  return STATUS_OPTIONS.some(item => item.value === value) ? value : 'draft'
}

export function normalizeFormat(format) {
  return String(format || '').trim().toLowerCase() === 'video' ? 'video' : 'image'
}

export function normalizeRegions(value) {
  if (Array.isArray(value)) {
    return value
      .map(item => String(item || '').trim().toUpperCase())
      .filter(Boolean)
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(item => item.trim().toUpperCase())
      .filter(Boolean)
  }
  return []
}

export function normalizeAudience(source = {}) {
  return {
    regions: normalizeRegions(source.regions || source.countries || ['US']),
    age: normalizeAgeRange(source.age),
    gender: String(source.gender || '').trim() || 'all',
  }
}

export function normalizeAds(sourceAds = [], fallbackLandingUrl = '') {
  const ads = Array.isArray(sourceAds) ? sourceAds.slice(0, 3) : []
  if (!ads.length) return [createAd(1)]

  return ads
    .map((item, index) => ({ item, order: Number(item && item.slot) || index + 1, index }))
    .sort((a, b) => {
      if (a.order === b.order) return a.index - b.index
      return a.order - b.order
    })
    .map((entry, index) => {
      const source = entry.item || {}
      return {
        ...createAd(index + 1),
        ...source,
        name: source.name || source.creative_name || `素材 ${index + 1}`,
        slot: index + 1,
        status: normalizeStatus(source.status),
        format: normalizeFormat(source.format),
        landing_url: source.landing_url || fallbackLandingUrl || '',
        utm: {
          ...createAd(index + 1).utm,
          ...(source.utm || {}),
        },
      }
    })
}

export function normalizeDetail(detail = {}, commercialId = '') {
  const base = createDefaultForm(commercialId)
  const flatLandingUrl = detail.landing_url || ''
  const campaignSource = detail.campaign || {}
  const adSetSource = detail.ad_set || {}
  const sourceAds = Array.isArray(detail.creatives) && detail.creatives.length
    ? detail.creatives
    : detail.ads

  return {
    ...base,
    id: detail.id || null,
    commercial_id: detail.commercial_id || commercialId,
    latest_payment_status: detail.latest_payment_status || '',
    latest_order_id: detail.latest_order_id || null,
    latest_settlement_amount: detail.latest_settlement_amount ?? null,
    latest_settlement_currency: detail.latest_settlement_currency || '',
    latest_payment_created_at: detail.latest_payment_created_at ?? null,
    latest_paid_at: detail.latest_paid_at ?? null,
    campaign: {
      ...base.campaign,
      ...campaignSource,
      name: detail.campaign_name || detail.name || campaignSource.name || base.campaign.name,
      objective: detail.objective || campaignSource.objective || base.campaign.objective,
      status: normalizeStatus(detail.status || campaignSource.status || base.campaign.status),
      budget_type: detail.budget_type || campaignSource.budget_type || base.campaign.budget_type,
      budget_amount: detail.budget_amount ?? campaignSource.budget_amount ?? base.campaign.budget_amount,
      currency: detail.currency || campaignSource.currency || base.campaign.currency,
      start_time: detail.start_time || campaignSource.start_time || base.campaign.start_time,
      end_time: detail.end_time || campaignSource.end_time || base.campaign.end_time,
    },
    ad_set: {
      ...base.ad_set,
      ...adSetSource,
      name: detail.interest_name || adSetSource.name || base.ad_set.name,
      conversion_location: detail.conversion_location || adSetSource.conversion_location || base.ad_set.conversion_location,
      optimization_goal: detail.optimization_goal || adSetSource.optimization_goal || base.ad_set.optimization_goal,
      landing_url: flatLandingUrl || adSetSource.landing_url || base.ad_set.landing_url,
      audience: normalizeAudience({
        ...(adSetSource.audience || {}),
        regions: detail.regions !== undefined ? detail.regions : (adSetSource.audience || {}).regions,
        age: detail.age !== undefined ? detail.age : (adSetSource.audience || {}).age,
        gender: detail.gender !== undefined ? detail.gender : (adSetSource.audience || {}).gender,
      }),
      placements: Array.isArray(adSetSource.placements) && adSetSource.placements.length
        ? adSetSource.placements
        : base.ad_set.placements,
    },
    ads: normalizeAds(sourceAds, flatLandingUrl || adSetSource.landing_url || ''),
  }
}

export function resolveCommercialId(data = {}) {
  return data.commercial_id
    || (data.commercial && (data.commercial.id || data.commercial.commercial_id))
    || data.id
    || ''
}

export function validateAge(value) {
  const range = normalizeAgeRange(value)
  return Array.isArray(range) && range.length === 2 && range[0] >= AGE_MIN && range[1] <= AGE_MAX
}

export function normalizeBillingSnapshot(source = {}) {
  const campaign = source && source.campaign && typeof source.campaign === 'object' ? source.campaign : source
  return {
    budget_type: String((campaign && campaign.budget_type) || 'daily').trim() || 'daily',
    budget_amount: Number((campaign && campaign.budget_amount) || 0),
    currency: String((campaign && campaign.currency) || 'USD').trim().toUpperCase() || 'USD',
  }
}

export function hasBillingSnapshotChanged(beforeValue, afterValue) {
  const before = normalizeBillingSnapshot(beforeValue)
  const after = normalizeBillingSnapshot(afterValue)
  return before.budget_type !== after.budget_type
    || before.currency !== after.currency
    || Number(before.budget_amount) !== Number(after.budget_amount)
}

export function promotionStatusLabel(status) {
  const normalized = normalizeStatus(status)
  const matched = STATUS_OPTIONS.find(item => item.value === normalized)
  return matched ? matched.label : (status || '-')
}

export function promotionObjectiveLabel(objective) {
  const matched = OBJECTIVE_OPTIONS.find(item => item.value === objective)
  return matched ? matched.label : (objective || '-')
}

export function formatMoney(amount, currency) {
  const numeric = Number(amount || 0)
  const code = String(currency || 'USD').toUpperCase()
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric)
  } catch (_) {
    return `${code} ${numeric.toFixed(2)}`
  }
}

export function formatDateTime(value, withSeconds = false) {
  if (!value) return '--'
  const date = typeof value === 'number' ? new Date(value) : new Date(Number(value))
  if (Number.isNaN(date.getTime())) return String(value)

  const pad = num => String(num).padStart(2, '0')
  const prefix = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  const suffix = withSeconds
    ? `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    : `${pad(date.getHours())}:${pad(date.getMinutes())}`

  return `${prefix} ${suffix}`
}

export function formatPromotionBudgetText(item = {}) {
  const amount = Number(item.budget_amount)
  if (!Number.isFinite(amount) || amount <= 0) return '未设置预算'
  const type = item.budget_type === 'lifetime' ? '总预算' : '单日预算'
  return `${type} ${String(item.currency || 'USD').toUpperCase()} ${amount.toFixed(2)}`
}

export function isPayableStatus(status) {
  return ['created', 'pending', 'processing'].includes(String(status || '').toLowerCase())
}

export function isRecreatableStatus(status) {
  return ['failed', 'cancelled', 'expired'].includes(String(status || '').toLowerCase())
}

export function isVideoAd(ad) {
  return !!(ad && (ad.format === 'video' || /\.(mp4|mov|webm|m4v)(\?|$)/i.test(ad.media_url || '')))
}
